import {
  ChangeDetectionStrategy,
  Component,
  computed,
  model,
  ModelSignal,
  OnChanges,
  Signal,
  signal,
  SimpleChanges,
  WritableSignal
} from '@angular/core';
import { SidebarWrapperComponent } from '../../../shared/components/wrappers/sidebar-wrapper/sidebar-wrapper.component';
import { SidebarPosition } from '../../../shared/enums/sidebar-wrapper.enum';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { ButtonSize, ButtonStyle } from '../../../shared/enums/button.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs';
import { RouterLink } from '@angular/router';
import { LoaderComponent } from '../../../shared/components/loaders/loader/loader.component';
import { WooCommerceProductApiService } from '../../services/woo-commerce/api/woo-commerce-product-api.service';
import { MealSelectionDialogComponent } from '../../../shared/components/dialogs/meal-selection-dialog/meal-selection-dialog.component';
import { Dialog } from '@angular/cdk/dialog';
import {
  WooCommercePopularProducts,
  WooCommercePopularProductsItem
} from '../../interfaces/woo-commerce/api/products/popular-product-api.interface';
import { FormattedCurrencyPipe } from '../../../shared/pipes/formatted-currency.pipe';
import { select, Store } from '@ngxs/store';
import { CartItemsAction } from '../../../shared/state/cart/cart.actions';
import {
  WooCommerceCartItem,
  WooCommerceCartTotals
} from '../../interfaces/woo-commerce/api/cart/cart.interface';
import { CartState } from '../../../shared/state/cart/cart.state';
import {
  RestaurantGeneralState,
  RestaurantState
} from '../../../shared/state/restaurant/restaurant.state';
import { CartSidebarItemComponent } from './cart-sidebar-item/cart-sidebar-item.component';
import { convertHtml2Text } from '../../../shared/utils/text.utils';
import {
  GA4Item,
  GoogleAnalyticsService
} from '../../services/google-analytics.service';
import { ProductImagePipe } from '../../../shared/pipes/product-image.pipe';
import { WooCommerceProduct } from '../../interfaces/woo-commerce/api/products/product-api.interface';
import { convertPopularProductsList } from '../../../shared/utils/popular-product-adapter.utils';

@UntilDestroy()
@Component({
  selector: 'app-cart-sidebar',
  imports: [
    SidebarWrapperComponent,
    ButtonComponent,
    RouterLink,
    LoaderComponent,
    FormattedCurrencyPipe,
    CartSidebarItemComponent,
    ProductImagePipe
  ],
  templateUrl: './cart-sidebar.component.html',
  styleUrl: './cart-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CartSidebarComponent implements OnChanges {
  isOpen: ModelSignal<boolean> = model<boolean>(false);

  // Restaurant
  restaurant: Signal<RestaurantGeneralState> = select(RestaurantState.general);

  // Cart
  cartItems: Signal<WooCommerceCartItem[]> = select(CartState.items);
  cartTotals: Signal<WooCommerceCartTotals> = select(CartState.totals);

  // Loading
  isCartLoading: Signal<boolean> = select(CartState.isLoading('cart'));
  isUpdateLoading: Signal<boolean> = select(CartState.isLoading('updateItem'));
  isDeleteLoading: Signal<boolean> = select(CartState.isLoading('deleteItem'));
  isSyncLoading: Signal<boolean> = select(CartState.isLoading('sync'));
  isAddItemLoading: Signal<boolean> = select(CartState.isLoading('addItem'));

  // TODO: Add to reducer
  isLoadingPopularProducts: WritableSignal<boolean> = signal<boolean>(false);
  popularProducts: WritableSignal<WooCommerceProduct[]> = signal<
    WooCommerceProduct[]
  >([]);

  filteredPopularProducts: Signal<WooCommerceProduct[]> = computed(
    () => this.filterProducts(this.cartItems(), this.popularProducts())
  );

  protected readonly ButtonSize = ButtonSize;
  protected readonly SidebarPosition = SidebarPosition;
  protected readonly ButtonStyle = ButtonStyle;
  protected readonly Number = Number;

  constructor(
    private dialog: Dialog,
    private store: Store,
    private wooCommerceProductApiService: WooCommerceProductApiService,
    private googleAnalyticsService: GoogleAnalyticsService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isOpen'] && changes['isOpen'].currentValue) {
      this.getCart();
      this.getPopularProducts();

      // Track view_cart event in Google Analytics when cart is opened
      this.trackViewCart();
    }
  }

  private trackViewCart(): void {
    try {
      const cartItems = this.cartItems();
      const totals = this.cartTotals();

      if (cartItems.length === 0) {
        return; // Don't track empty cart views
      }

      const currencyDivider = 10 ** (totals.currency_minor_unit || 2);

      const items: GA4Item[] = cartItems.map((item, index) => ({
        item_id: item.id.toString(),
        item_name: convertHtml2Text(item.name),
        price: parseFloat(item.prices.price) / currencyDivider,
        quantity: item.quantity,
        index,
        item_variant:
          item.variation
            ?.map((v) => v.value)
            .filter(Boolean)
            .join(', ') || undefined
      }));

      const totalValue = parseFloat(totals.total_items) / currencyDivider;

      this.googleAnalyticsService.trackViewCart({
        currency: totals.currency_code,
        value: totalValue,
        items: items
      });
    } catch (error) {
      console.error('Failed to track view cart:', error);
    }
  }

  openMealSelectionModal(data: WooCommerceProduct): void {
    this.dialog.open(MealSelectionDialogComponent, {
      width: '100%',
      maxWidth: '800px',
      panelClass: 'responsive-modal',
      data
    });
  }

  closeSidebar() {
    this.isOpen.set(false);
  }

  updateProductQuantity(key: string, quantity: number) {
    this.store.dispatch(
      new CartItemsAction.Update({ key, quantity })
    );
  }

  removeProduct(productKey: string) {
    this.store.dispatch(new CartItemsAction.Remove(productKey));
  }

  private getCart() {
    this.store.dispatch(new CartItemsAction.Get());
  }

  private getPopularProducts() {
    this.isLoadingPopularProducts.set(true);
    this.wooCommerceProductApiService
      .getPopularProducts()
      .pipe(
        untilDestroyed(this),
        finalize(() => this.isLoadingPopularProducts.set(false))
      )
      .subscribe({
        next: (popular: WooCommercePopularProducts): void => {
          // Convert popular products to standard WooCommerceProduct
          const convertedProducts = convertPopularProductsList(popular.products);
          this.popularProducts.set(convertedProducts);
        }
      });
  }

  private filterProducts(
    cartItems: WooCommerceCartItem[] = [],
    popularProducts: WooCommerceProduct[] = []
  ): WooCommerceProduct[] {
    const cartItemsObject: Record<string, WooCommerceCartItem> =
      cartItems.reduce(
        (
          acc: Record<string, WooCommerceCartItem>,
          cartItem: WooCommerceCartItem
        ): Record<string, WooCommerceCartItem> => {
          acc[cartItem.id] = cartItem;
          return acc;
        },
        {}
      );

    return popularProducts.filter(
      (product: WooCommerceProduct) => !cartItemsObject[product.id]
    );
  }
}
